import { z } from 'zod';
import { hexColourSchema } from '../../color.js';
import { domainSchema, MAX_DOMAINS_PER_SAVE } from '../../domain.js';
import { billingIntervalSchema, discountCodeSchema, PLAN_IDS } from '../../billing.js';
import { industrySchema } from '../../industry.js';
import { suggestedQuestionsSchema, websiteNameSchema } from '../../website.js';
/**
 * The nine website-setup dashboard endpoints (`technical.md`, API contracts).
 * All paths follow `patterns/api-conventions.md`, 3. Error shapes follow
 * `patterns/error-handling.md`, 1. Permission and tenant scoping follow
 * `patterns/auth-patterns.md`, 2--4.
 */
const websiteParams = z.object({ websiteId: z.uuid() });
// ---------------------------------------------------------------------------
// Response schema
// ---------------------------------------------------------------------------
export const websiteResponseSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    domains: z.array(z.string()),
    industry: industrySchema,
    accentColour: z.string().nullable(),
    websiteKey: z.string().nullable(),
    script: z.string().nullable(),
    setupStep: z.number(),
    hasContactDetails: z.boolean(),
    /** The questions the widget offers visitors: the owner's, or Groundly's defaults. */
    suggestedQuestions: z.array(z.string()),
    /** `true` while the website uses Groundly's default questions for its industry. */
    suggestedQuestionsAreDefault: z.boolean(),
    createdAt: z.string().datetime(),
});
// ---------------------------------------------------------------------------
// List websites
// ---------------------------------------------------------------------------
export const listWebsitesResponseSchema = z.object({
    items: z.array(z.object({
        id: z.uuid(),
        name: z.string(),
        setupStep: z.number(),
    })),
});
/** @AnyAccountUser() */
export const listWebsites = {
    method: 'GET',
    path: '/api/dashboard/websites',
    params: z.object({}),
    query: z.object({}),
    body: z.object({}),
    response: listWebsitesResponseSchema,
};
// ---------------------------------------------------------------------------
// Create website (guided step 1)
// ---------------------------------------------------------------------------
export const createWebsiteBodySchema = z.object({
    name: websiteNameSchema,
    domains: z.array(domainSchema).min(1, 'Add at least one domain.').max(MAX_DOMAINS_PER_SAVE),
    /** The website's plan, from today; its first billing cycle is prorated (`product/business-rules.md`, Billing). */
    planId: z.enum(PLAN_IDS, { error: 'Choose a plan.' }),
    /** Monthly (the default) or yearly: yearly starts with the next cycle, today's part cycle billed monthly. */
    billingInterval: billingIntervalSchema.optional(),
    /** A discount code for this website: it is billed at the list price less the code's discount. */
    discountCode: discountCodeSchema.optional(),
});
/** @RequirePermission('website_setup') */
export const createWebsite = {
    method: 'POST',
    path: '/api/dashboard/websites',
    params: z.object({}),
    query: z.object({}),
    body: createWebsiteBodySchema,
    response: websiteResponseSchema,
};
// ---------------------------------------------------------------------------
// Get website
// ---------------------------------------------------------------------------
/** @RequirePermission('website_setup') */
export const getWebsite = {
    method: 'GET',
    path: '/api/dashboard/websites/:websiteId',
    params: websiteParams,
    query: z.object({}),
    body: z.object({}),
    response: websiteResponseSchema,
};
// ---------------------------------------------------------------------------
// Update website (name and domains)
// ---------------------------------------------------------------------------
export const updateWebsiteBodySchema = z
    .object({
    name: websiteNameSchema.optional(),
    domains: z
        .array(domainSchema)
        .min(1, 'A website must have at least one domain.')
        .max(MAX_DOMAINS_PER_SAVE)
        .optional(),
})
    .refine((data) => data.name !== undefined || data.domains !== undefined, {
    message: 'Provide a name or domains to update.',
});
/** @RequirePermission('website_setup') */
export const updateWebsite = {
    method: 'PATCH',
    path: '/api/dashboard/websites/:websiteId',
    params: websiteParams,
    query: z.object({}),
    body: updateWebsiteBodySchema,
    response: websiteResponseSchema,
};
// ---------------------------------------------------------------------------
// Update industry
// ---------------------------------------------------------------------------
export const updateIndustryBodySchema = z.object({
    industry: industrySchema,
});
/** @RequirePermission('website_setup') */
export const updateIndustry = {
    method: 'PATCH',
    path: '/api/dashboard/websites/:websiteId/industry',
    params: websiteParams,
    query: z.object({}),
    body: updateIndustryBodySchema,
    response: websiteResponseSchema,
};
// ---------------------------------------------------------------------------
// Update branding
// ---------------------------------------------------------------------------
export const updateBrandingBodySchema = z.object({
    accentColour: hexColourSchema.nullable(),
});
/** @RequirePermission('website_setup') */
export const updateBranding = {
    method: 'PATCH',
    path: '/api/dashboard/websites/:websiteId/branding',
    params: websiteParams,
    query: z.object({}),
    body: updateBrandingBodySchema,
    response: websiteResponseSchema,
};
// ---------------------------------------------------------------------------
// Skip branding (guided step 3)
// ---------------------------------------------------------------------------
/** @RequirePermission('website_setup') */
export const skipBranding = {
    method: 'POST',
    path: '/api/dashboard/websites/:websiteId/skip-branding',
    params: websiteParams,
    query: z.object({}),
    body: z.object({}),
    response: z.void(),
};
// ---------------------------------------------------------------------------
// Complete setup (guided step 4)
// ---------------------------------------------------------------------------
/** @RequirePermission('website_setup') */
export const completeSetup = {
    method: 'POST',
    path: '/api/dashboard/websites/:websiteId/complete-setup',
    params: websiteParams,
    query: z.object({}),
    body: z.object({}),
    response: z.void(),
};
// ---------------------------------------------------------------------------
// Delete website
// ---------------------------------------------------------------------------
export const deleteWebsiteBodySchema = z.object({
    confirmDomain: z.string(),
});
/** @RequirePermission('website_setup') */
export const deleteWebsite = {
    method: 'DELETE',
    path: '/api/dashboard/websites/:websiteId',
    params: websiteParams,
    query: z.object({}),
    body: deleteWebsiteBodySchema,
    response: z.void(),
};
// ---------------------------------------------------------------------------
// Suggested questions (the widget's welcome)
// ---------------------------------------------------------------------------
export const updateSuggestedQuestionsBodySchema = z.object({
    /** `null` goes back to Groundly's defaults for the website's industry; `[]` shows none. */
    questions: suggestedQuestionsSchema.nullable(),
});
/** @RequirePermission('website_setup') */
export const updateSuggestedQuestions = {
    method: 'PATCH',
    path: '/api/dashboard/websites/:websiteId/suggested-questions',
    params: websiteParams,
    query: z.object({}),
    body: updateSuggestedQuestionsBodySchema,
    response: websiteResponseSchema,
};
//# sourceMappingURL=websites.js.map